import { api, ApiError } from "../api.js";
import { esc, cohortIcons, displayName, formatDateTime, toast } from "../utils.js";
import { postCardHtml, attachPostCardHandlers } from "../postCard.js";
import { getCurrentUser, isSiteAdmin } from "../auth.js";
import { confirmAction } from "../confirmDialog.js";
import { followSwitchHtml, attachFollowSwitch } from "../followSwitch.js";
import { navigate } from "../app.js";
import {
  photoUploadHtml,
  attachPhotoUpload,
  infiniteGallery,
} from "../photoGallery.js";
import { tabSelectorHtml, attachTabSelector } from "../tabbedSection.js";
import { manageMenuHtml } from "../manageMenu.js";

export async function render(container, params) {
  const planId = params.id;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  async function load() {
    const [plan, posts, photoPage] = await Promise.all([
      api.get(`/dive-plans/${planId}`),
      api.get("/posts", { dive_plan_id: planId }),
      api.getPage(`/dive-plans/${planId}/photos`, { limit: 1 }),
    ]);
    return { plan, posts, photoCount: photoPage.total ?? 0 };
  }

  let plan, posts, photoCount;
  try {
    ({ plan, posts, photoCount } = await load());
  } catch (err) {
    container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Dive plan not found.</div></div>`;
    return;
  }

  let activeTab = "posts";
  // The gallery pages in as you scroll; stopped when the tab changes.
  let activeGallery = null;

  function myStatus() {
    const me = getCurrentUser();
    if (!me || !plan.participants) return null;
    const mine = plan.participants.find((p) => p.user.id === me.id);
    return mine ? mine.status : null;
  }

  function isOrganizer() {
    const me = getCurrentUser();
    return me != null && plan.organizer != null && me.id === plan.organizer.id;
  }

  async function deletePlan() {
    // The app's own dialog rather than window.confirm — see
    // confirmDialog.js. This was the last caller of the native one.
    const asModerator = !isOrganizer();
    const confirmed = await confirmAction({
      title: asModerator ? "Remove this dive event?" : "Delete this dive event?",
      body:
        (asModerator
          ? "You are removing someone else's event as a moderator. "
          : "") +
        "This removes it for everyone who joined. Posts about it will stay, but will no longer be linked to it.",
      confirmLabel: asModerator ? "Remove" : "Delete",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/dive-plans/${planId}`);
      navigate("/dive-plans");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not delete this event.", "danger");
    }
  }

  /** Paints the active tab, its action button and its panel.
   *
   * Panels are rebuilt on switch rather than hidden, so handlers bind to
   * the elements actually on screen. */
  function renderTabs() {
    const host = container.querySelector("#plan-tabs-host");
    const panel = container.querySelector("#plan-tab-panel");
    const action = container.querySelector("#plan-tab-action");
    if (!host || !panel) return;

    host.innerHTML = tabSelectorHtml(
      [
        { id: "posts", label: "Posts", count: posts.length },
        { id: "photos", label: "Photos", count: photoCount },
      ],
      activeTab,
      { id: "plan-tabs" }
    );
    attachTabSelector(host, (id) => {
      activeTab = id;
      renderTabs();
    });

    if (activeTab === "posts") {
      action.innerHTML = `<button class="btn btn-sm btn-outline-primary" id="post-about-btn"><i class="bi bi-plus-lg me-1"></i>Post</button>`;
      panel.innerHTML = posts.length
        ? posts.map(postCardHtml).join("")
        : `<p class="text-slate mb-0">No posts about this dive yet.</p>`;
      attachPostCardHandlers(panel);
      action.querySelector("#post-about-btn").addEventListener("click", () => {
        // Carry the event's whole context across: the site and the cohort
        // it is organised under, not just the event id.
        const q = new URLSearchParams();
        q.set("dive_plan_id", plan.id);
        if (plan.dive_site) q.set("dive_site_id", plan.dive_site.id);
        if (plan.community) q.set("community_id", plan.community.id);
        navigate(`/create-post?${q.toString()}`);
      });
    } else {
      // Uploading needs an account; a button that only ever 401s is
      // worse than no button.
      action.innerHTML = getCurrentUser() ? photoUploadHtml("plan-photo-add") : "";
      attachPhotoUpload(action, {
        id: "plan-photo-add",
        endpoint: `/dive-plans/${planId}/photos`,
        onUploaded: async () => {
          photoCount = (await api.getPage(`/dive-plans/${planId}/photos`, { limit: 1 })).total ?? 0;
          renderTabs();
        },
      });
      const me = getCurrentUser();
      activeGallery?.destroy();
      activeGallery = infiniteGallery(panel, {
        path: `/dive-plans/${planId}/photos`,
        emptyMessage:
          "No photos from this dive yet. Photos attached to posts about it show up in this gallery.",
        // Whoever uploaded it, or a moderator — the same rule the API
        // enforces.
        canDelete: (photo) => me != null && (photo.owner_id === me.id || isSiteAdmin()),
        onDeleted: async () => renderTabs(),
      });
    }
  }

  function renderAll() {
    const status = myStatus();
    const isGoing = status === "going";
    const isInterested = status === "interested";
    const joined = plan.max_participants
      ? `${plan.participant_count}/${plan.max_participants} joined`
      : `${plan.participant_count} joined`;
    // The organizer edits and deletes; a moderator may remove someone
    // else's event but not rewrite it. The same split the post page
    // uses, and the same one the API enforces.
    const canEdit = isOrganizer();
    const canDelete = isOrganizer() || isSiteAdmin();

    container.innerHTML = `
      <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
      <div class="card mb-4">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <h1 class="display-heading h4 mb-0">${esc(plan.title)}</h1>
            <div class="d-flex align-items-center gap-2">
              <span class="badge text-bg-${plan.is_past ? "secondary" : "primary"}">${plan.is_past ? "Past dive" : "Upcoming dive"}</span>
              ${manageMenuHtml(
                [
                  canEdit && { id: "edit-plan-btn", icon: "bi-pencil", label: "Edit" },
                  canDelete && {
                    id: "delete-plan-btn",
                    icon: "bi-trash",
                    label: isOrganizer() ? "Delete" : "Remove as moderator",
                    danger: true,
                  },
                ],
                { label: "Manage this dive event" }
              )}
            </div>
          </div>
          <div class="text-slate mb-1"><i class="bi bi-geo-alt me-1"></i>${
            plan.dive_site ? esc(plan.dive_site.name) : "Site to be determined"
          }</div>
          ${plan.community ? `<a href="#/cohort/${plan.community.id}" class="badge text-bg-light border mb-1 text-decoration-none d-inline-block">${cohortIcons(plan.community)}${esc(plan.community.name)}</a>` : ""}
          <div class="text-slate mb-1"><i class="bi bi-clock me-1"></i>${formatDateTime(plan.planned_date)}</div>
          <div class="text-slate mb-1"><i class="bi bi-people me-1"></i>${joined}</div>
          ${plan.organizer ? `<div class="text-slate mb-2"><i class="bi bi-person me-1"></i>Organized by ${esc(displayName(plan.organizer))}</div>` : ""}
          ${plan.description ? `<p class="mt-3 mb-0 dc-user-text">${esc(plan.description)}</p>` : ""}
          ${!plan.is_past ? `
            <div class="d-flex gap-2 mt-3">
              <button class="btn btn-primary flex-fill" id="join-btn">
                <i class="bi bi-check-lg me-1"></i>${isGoing ? "You're going" : "Join dive"}
              </button>
              <button class="btn btn-outline-secondary flex-fill" id="interest-btn">
                <i class="bi ${isGoing || isInterested ? "bi-x-lg" : "bi-star"} me-1"></i>${isGoing || isInterested ? "Leave" : "Interested"}
              </button>
            </div>` : ""}
          ${isGoing || isInterested ? followSwitchHtml({
            following: plan.is_following,
            label: "Follow this dive event",
            help: "Posts about it appear in your Following feed. Turn this off to mute the event.",
          }) : ""}
        </div>
      </div>
      <div class="card mb-4">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div id="plan-tabs-host"></div>
            <div id="plan-tab-action"></div>
          </div>
          <div id="plan-tab-panel"></div>
        </div>
      </div>`;

    container.querySelector("#back-btn").addEventListener("click", () => history.back());
    attachFollowSwitch(container, {
      path: `/dive-plans/${planId}`,
      onChange: (updated) => (plan.is_following = updated.is_following),
    });
    renderTabs();

    const editBtn = container.querySelector("#edit-plan-btn");
    if (editBtn) editBtn.addEventListener("click", () => navigate(`/dive-plan/${planId}/edit`));
    const deleteBtn = container.querySelector("#delete-plan-btn");
    if (deleteBtn) deleteBtn.addEventListener("click", deletePlan);

    const joinBtn = container.querySelector("#join-btn");
    const interestBtn = container.querySelector("#interest-btn");
    if (joinBtn) {
      joinBtn.addEventListener("click", async () => {
        joinBtn.disabled = true;
        try {
          await api.post(`/dive-plans/${planId}/join`, { status: "going" });
          ({ plan, posts, photoCount } = await load());
          renderAll();
        } catch (err) {
          toast(err instanceof ApiError ? err.message : "Could not join.", "danger");
          joinBtn.disabled = false;
        }
      });
    }
    if (interestBtn) {
      interestBtn.addEventListener("click", async () => {
        interestBtn.disabled = true;
        try {
          if (isGoing || isInterested) {
            await api.post(`/dive-plans/${planId}/leave`);
          } else {
            await api.post(`/dive-plans/${planId}/join`, { status: "interested" });
          }
          ({ plan, posts, photoCount } = await load());
          renderAll();
        } catch (err) {
          toast(err instanceof ApiError ? err.message : "Something went wrong.", "danger");
          interestBtn.disabled = false;
        }
      });
    }
  }

  renderAll();
}
